/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { HttpClient, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, filter, forkJoin, map, Observable, of, switchMap } from 'rxjs';

import { LambdaClient } from '@aws-sdk/client-lambda';

import { AttachmentTypes, IAttachment, WebAppAttachmentTypes } from '../../modules/chat/interfaces/IAttachment';
import { skipApiKey } from './http.context';
import { MessagingEnvironmentService } from './messaging-environment.service';

@Injectable()
export class AwsService {
  uploadingProgressSubject = new BehaviorSubject<number>(0);
  uploadingProgress$ = this.uploadingProgressSubject.asObservable();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  lambda = new LambdaClient({ region: 'us-west-2' });
  constructor(private env: MessagingEnvironmentService, private http: HttpClient) {}

  uploadFile(file: File, type: AttachmentTypes, size: number): Observable<IAttachment> {
    const generatedKey = new Date().toISOString().replace(/[:.-]/g, '').toLowerCase() + crypto.randomUUID();
    const fileExtension = file.name.split('.').pop();
    const titleWithExtension = `${generatedKey}.${fileExtension}`;
    return this.getPresignedUrl(titleWithExtension).pipe(
      switchMap((data) => {
        const req = new HttpRequest('PUT', data.uploadUrl, file, {
          reportProgress: true,
          headers: new HttpHeaders({ 'Content-Type': file.type }),
          context: skipApiKey(),
        });
        return this.http.request(req).pipe(
          map((evt) => {
            if (evt.type === HttpEventType.UploadProgress && evt.total) {
              const pct = Math.round((evt.loaded / evt.total) * 100);
              this.uploadingProgressSubject.next(pct);
            }
            if (evt.type === HttpEventType.Response) {
              return data.uploadUrl;
            }
            return null;
          }),
          filter((url): url is string => url !== null),
          map(() => {
            const thumbnailURL = this.env.env.imagesCdn + '/' + titleWithExtension;
            return { type: type, title: file.name, thumbnailURL: thumbnailURL, size };
          })
        );
      })
    );
  }

  getPresignedUrl(fileName: string): Observable<{ uploadUrl: string; downloadUrl: string }> {
    const endpoint = `${this.env.env.notificationApi}/media/upload`;
    return this.http.post<{ uploadUrl: string; downloadUrl: string }>(
      endpoint,
      { fileName },
      {
        context: skipApiKey(),
      }
    );
  }

  resetProgress() {
    this.uploadingProgressSubject.next(0);
  }

  uploadFiles(attachments: IAttachment[]): Observable<IAttachment[]> {
    if (!attachments.length) {
      return of([]);
    }
    const gifs = attachments.filter((attch) => !attch.file);
    if (gifs.length) {
      return of(
        gifs.map((attch) => {
          return {
            uid: attch.uid,
            title: attch.title,
            thumbnailURL: attch.thumbnailURL,
            type: attch.type,
            size: attch.size,
          } as IAttachment;
        })
      );
    }

    const uploads: Observable<IAttachment>[] = [];
    for (const attachment of attachments) {
      if (!attachment.file) {
        continue;
      }
      uploads.push(this.uploadFile(attachment.file, attachment.type, attachment.size ?? 0));
    }
    if (!uploads.length) {
      return of([]);
    }
    return forkJoin(uploads);
  }

  getFileType(fileName: string): WebAppAttachmentTypes | undefined {
    const fileExtension = fileName.split('.').pop()?.toUpperCase();
    if (fileExtension && Object.values(WebAppAttachmentTypes).includes(fileExtension as WebAppAttachmentTypes)) {
      return fileExtension as WebAppAttachmentTypes;
    }
    return undefined;
  }
}
